import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Button, ButtonIntent, ButtonSize } from 'app/design-system/components/Button';
import { Shake } from 'app/design-system/components/Shake';
import { DictionaryData, useTranslation } from 'app/lang';
import { selectAccountBalance } from 'app/lib/account/selectors';
import { StakeType } from 'app/lib/staking/constants';

import { ClearBetsInfoError, clearBetsInfoErrorByBetIds } from '../../actions';
import {
  RESOLVE_BY_ACCEPT_AVAILABILITY,
  RESOLVE_BY_CANCEL_INSERT,
  RESOLVE_BY_REPLACE_ENTRIES,
  ResolveActions,
} from '../../actions/conflict';
import { AcceptPriceChanges, acceptPriceChanges, deleteUnavailableBets } from '../../actions/entries';
import { ConflictCase, SubmissionState } from '../../constants';
import { RecordOfConflict } from '../../records';
import { selectHasEnoughMoney, selectTotalAmount } from '../../selectors';
import { selectLastTriggeredTimeState } from '../../selectors/conflict';
import {
  selectAnyHasLockedState,
  selectBets,
  selectHasPriceChanges,
  selectSomeHasError,
  selectTotalAmountActive,
} from '../../selectors/entries';
import { selectCurrentStakeType } from '../../selectors/stakeTypes';
import { selectSingleErrorsBetIds } from '../../selectors/stakeTypes/single';
import { selectConflictState } from '../../selectors/state';
import { selectSubmission } from '../../selectors/submission';
import type { ConflictState } from '../../types/conflict';
import TextWithAmountBar from '../TextWithAmountBar';
import ConflictComponent from './ConflictComponent';
import ConflictEntry from './Entry';

import dictionary from './Conflicts.dictionary.json';

import styles from './Conflicts.module.scss';

// ---

const DEFAULT_ACTION_LABELS_DICTIONARY = {
  [RESOLVE_BY_CANCEL_INSERT]: dictionary.resolveByCancelInsert,
  [RESOLVE_BY_REPLACE_ENTRIES]: dictionary.resolveByReplaceEntries,
  [RESOLVE_BY_ACCEPT_AVAILABILITY]: dictionary.defaultResolveByAcceptAvailability,
};

type ConflictMessageDictionary = Record<ConflictCase, {
  title: DictionaryData;
  actions?: Record<string, DictionaryData>;
}>;

const CONFLICT_MESSAGE_DICTIONARY: ConflictMessageDictionary = {
  [ConflictCase.SINGLE]: {
    title: dictionary.singleTitle,
  },
  [ConflictCase.EXPRESS]: {
    title: dictionary.expressTitle,
    actions: {
      [RESOLVE_BY_ACCEPT_AVAILABILITY]: dictionary.resolveByAcceptAvailability,
    },
  },
  [ConflictCase.SYSTEM]: {
    title: dictionary.systemTitle,
    actions: {
      [RESOLVE_BY_ACCEPT_AVAILABILITY]: dictionary.resolveByAcceptAvailability,
    },
  },
};

// ---

type StoreProps = {
  amount: number;
  showNoMoneyError: boolean;
  hasPriceChanges: boolean;
  hasEntriesErrors: boolean;
  disabled: boolean;
  conflict: ConflictState;
  lastTriggeredTime: RecordOfConflict['lastTriggeredTime'];
  singleErrorsBetIds: number[];
};

type DispatchProp = {
  dispatch: ThunkDispatchType<AcceptPriceChanges | ResolveActions | ClearBetsInfoError>;
};

const Conflict: FC<DispatchProp & StoreProps> = ({
  amount,
  showNoMoneyError,
  hasPriceChanges,
  hasEntriesErrors,
  disabled,
  conflict,
  lastTriggeredTime,
  singleErrorsBetIds,
  dispatch,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const topUpBalanceHandler = useCallback(() => {
    history.push('/personal/deposit');
  }, []);

  const acceptPricesChangedHandler = useCallback(() => {
    dispatch(acceptPriceChanges());
  }, []);

  const deleteUnavailableBetsHandler = useCallback(() => {
    dispatch(deleteUnavailableBets());
  }, []);

  const restoreBetsHandler = useCallback(() => {
    dispatch(clearBetsInfoErrorByBetIds(singleErrorsBetIds));
  }, [singleErrorsBetIds]);

  const resolveConflictHandler = useCallback((action: ResolveActions) => () => {
    dispatch(action);
  }, []);

  if (conflict !== null) {
    const message = conflict.case && CONFLICT_MESSAGE_DICTIONARY[conflict.case];

    return (
      <div className={styles.conflictContainer}
           data-test="entriesConflict-container">
        <span className={styles.title}
              data-test="entriesConflict-title">
          {message ? t(message.title) : t(dictionary.defaultConflictTitle)}
        </span>

        <Shake trigger={lastTriggeredTime}>
          <div className={styles.shakeWrapper}>
            {conflict.items.valueSeq().map((entry) => (
              <ConflictEntry key={entry.bet.entity.id}
                             entry={entry} />
            ))}

            {conflict.actions.map((action, index) => {
              const label = message?.actions?.[action.type]
                || DEFAULT_ACTION_LABELS_DICTIONARY[action.type];

              return (
                <Button key={index}
                        intent={ButtonIntent.PRIMARY}
                        size={ButtonSize.SMALL}
                        className={styles.conflictButton}
                        data-test={`entriesConflict-actionButton actionButton-${action.type}`}
                        onClick={resolveConflictHandler(action)}>
                  {t(label)}
                </Button>
              );
            })}
          </div>
        </Shake>
      </div>
    );
  }

  if (disabled || (!showNoMoneyError && !hasPriceChanges && !hasEntriesErrors)) {
    return null;
  }

  const NoMoneyComponent = (
    <TextWithAmountBar className={styles.noMoneyComponent}
                       value={amount}
                       label={dictionary.noMoneyLabel}
                       data-test="conflicts-noMoney" />
  );

  return (
    <div className={styles.container}
         data-test="conflicts-container">
      {showNoMoneyError && (
        <ConflictComponent label={NoMoneyComponent}
                           data-test="conflicts-topUpBalance"
                           buttonText={t(dictionary.noMoneyErrorText)}
                           onClick={topUpBalanceHandler} />
      )}

      {hasPriceChanges && (
        <ConflictComponent label={t(dictionary.priceChangesLabel)}
                           data-test="conflicts-priceChanged"
                           buttonText={t(dictionary.priceChangesText)}
                           onClick={acceptPricesChangedHandler} />
      )}

      {(hasEntriesErrors && Boolean(singleErrorsBetIds.length)) && (
        <ConflictComponent label={t(dictionary.restoreEntriesErrorsLabel)}
                           data-test="conflicts-hasRestoreBets"
                           buttonText={t(dictionary.restoreEntriesErrorsText)}
                           onClick={restoreBetsHandler} />
      )}

      {hasEntriesErrors && (
        <ConflictComponent label={t(dictionary.entriesErrorsLabel)}
                           data-test="conflicts-hasUnavailableBets"
                           buttonText={t(dictionary.entriesErrorsText)}
                           onClick={deleteUnavailableBetsHandler} />
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const betsSize = selectBets(state).size;

  let amount = 0;
  const hasEnoughMoney = selectHasEnoughMoney(state);
  const totalAmountActive = selectTotalAmountActive(state);

  const stakeType = selectCurrentStakeType(state);

  const showNoMoneyError =
    !hasEnoughMoney && betsSize > 1
    && !totalAmountActive && stakeType.has(StakeType.SINGLE);

  if (showNoMoneyError) {
    const balance = selectAccountBalance(state) || 0;
    const totalAmount = selectTotalAmount(state);

    amount = totalAmount - balance;
  }

  return {
    amount,
    showNoMoneyError,
    hasPriceChanges: selectHasPriceChanges(state),
    hasEntriesErrors: selectSomeHasError(state) || selectAnyHasLockedState(state),
    disabled: selectSubmission(state) !== SubmissionState.AWAITING,
    conflict: selectConflictState(state),
    lastTriggeredTime: selectLastTriggeredTimeState(state),
    singleErrorsBetIds: selectSingleErrorsBetIds(state),
  };
};

export default connect(mapStateToProps)(Conflict);
